import React, {useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';
import {ColorSwatchSelectedComponent} from 'components/customer/Room/ColorSwatches';
import {
    Door,
    Material,
    MaterialEdge,
} from 'components/customer/Materials/entity';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import {Image, Stack} from 'react-bootstrap';
import {isMobile} from 'react-device-detect';
import {transformDisplayName} from 'components/customer/Materials/helper';
import {Icon} from 'shared/helpers';
import {SelectionsType} from 'components/customer/JobDashboard/columns/SelectionsOverview';
import {tokenizeString} from 'shared/tokenizer';

interface MaterialProps {
    material?: Material;
    edge?: MaterialEdge;
    isHorizontalGrain?: boolean;
    type: SelectionsType;
    door?: Door;
    setActiveSelectionType?: React.Dispatch<
        React.SetStateAction<SelectionsType | null>
    >;
    showDetail?: boolean;
    title?: string;
    customWidth?: number;
}

const NON_SUPPLY = 7;

export const Preview = ({
    type,
    material,
    edge,
    isHorizontalGrain,
    door,
    setActiveSelectionType,
    title,
    showDetail = true,
    customWidth,
}: MaterialProps) => {
    const [showFallback, setShowFallback] = useState(false);
    const materialTransformed = useMemo(
        () => material && transformDisplayName(material),
        [material]
    );

    const handleClick = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            if (setActiveSelectionType) {
                setActiveSelectionType((prev) => {
                    // if already active, deselect
                    return prev === type ? null : type;
                });
            }
        },
        [setActiveSelectionType]
    );

    const formattedDoorName = useMemo(() => {
        if (type == SelectionsType.STYLE) {
            const doorName = door?.image?.name;
            if (
                typeof doorName == 'string' &&
                doorName.indexOf('/uploads') > -1
            ) {
                return door.image.name;
            }

            return `/uploads/images/Door - Drawer Face${door.image.name}`;
        }
    }, [door, type]);

    const handleError = useCallback(() => {
        setShowFallback(true);
    }, []);

    const doorStyleWidth = useMemo(() => {
        if (customWidth) {
            return customWidth;
        }

        if (isMobile) {
            return 30;
        }

        return 50;
    }, [customWidth, isMobile]);

    if (material && material.type.id == NON_SUPPLY) {
        return null;
    }

    return (
        <div>
            <Label>{title ? title : type}</Label>
            <OverlayTrigger
                placement="top"
                overlay={
                    <div data-cy="material-tooltip">
                        {material
                            ? parseHtmlString(materialTransformed.displayName)
                            : door.name}
                    </div>
                }>
                <Stack>
                    {material ? (
                        <ColorContainer
                            data-cy="material-tooltip-trigger"
                            onClick={handleClick}>
                            <ColorSwatchSelectedComponent
                                selectedColor={material}
                                selectedEdge={edge}
                                isEdge={false}
                                horizontal={isHorizontalGrain}
                                size={isMobile ? 30 : 50}
                                isSmallThumbnail={true}
                            />
                        </ColorContainer>
                    ) : door.image.name && !showFallback ? (
                        <Image
                            onClick={handleClick}
                            src={formattedDoorName}
                            alt={door.name}
                            style={{
                                width: `${doorStyleWidth}px`,
                                cursor: 'pointer',
                            }}
                            onError={handleError}
                        />
                    ) : (
                        <Icon
                            iconName="Layout-Add-Door.svg"
                            onClick={handleClick}
                            style={{
                                width: `${doorStyleWidth}px`,
                                cursor: 'pointer',
                            }}
                        />
                    )}

                    {showDetail ? (
                        <MiniDetail>
                            {door
                                ? door.name
                                : parseHtmlString(
                                      `${material?.thickness}mm <em>${tokenizeString(material?.finish)}</em>`
                                  )}
                        </MiniDetail>
                    ) : null}
                </Stack>
            </OverlayTrigger>
        </div>
    );
};

export const MiniDetail = styled.div`
    text-align: left;
    margin-top: 10px;
    max-width: 70px;
    font-size: 0.85em;
    word-break: keep-all;
`;

export const Label = styled.div`
    color: ${({theme}) => theme.colors.primary.main};
    font-weight: 500;
    text-align: left;
`;

const ColorContainer = styled.div`
    > div {
        margin-left: 7px;
        margin-top: 0;
    }
`;
